import { createApi } from "@reduxjs/toolkit/query/react";
import { chargerActionType } from "redux/utils/types";
import { baseQueryWithReauth } from "./utils/constants";
import {
	ChargersParams,
	ChargersResponse,
	Charger,
	ChargerInfoResponse,
	ChargerTransactionResponse,
	ChargerTransaction ,
	AssignCharger
} from "./utils/interfaces";
import moment from "moment";

const getUrlParams = (args) => {
	if (!args.sortBy && !args.filterBy) {
		return `limit=${args.limit}&offset=${args.offset}`;
	} else if (args.sortBy && !args.filterBy) {
		return `limit=${args.limit}&sortBy=${args.sortBy}&offset=${args.offset}`;
	} else if (!args.sortBy && args.filterBy) {
		return `limit=${args.limit}&offset=${args.offset}&filterBy=${args.filterBy}`;
	} else if (args.sortBy && args.filterBy) {
		return `limit=${args.limit}&sortBy=${args.sortBy}&offset=${args.offset}&filterBy=${args.filterBy}`;
	}
	return `limit=${args.limit}&offset=${args.offset}`;
};

const getUrlParamsForTransactions = (args) => {
	if (!args.sortBy) {
		return `userName=${args.userName}&locationId.stationId=${args.locationIdStationId}&limit=${args.limit}&offset=${args.offset}&chargerId=${args.chargerId}`;
	}
	return `userName=${args.userName}&locationId.stationId=${args.locationIdStationId}&limit=${args.limit}&sortBy=${args.sortBy}&offset=${args.offset}&chargerId=${args.chargerId}`;
};

const getUrlParamsForUserTransactions = (args) => {
	if (!args.sortBy) {
		return `userName=${args.userName}&locationId.stationId=${args.locationIdStationId}&limit=${args.limit}&offset=${args.offset}&userId=${args.chargerId}`;
	}
	return `userName=${args.userName}&locationId.stationId=${args.locationIdStationId}&limit=${args.limit}&sortBy=${args.sortBy}&offset=${args.offset}&userId=${args.chargerId}`;
};

const getUserInfoParams = (args) => {
	return `locationId.stationId=${args.locationIdStationId}&userId=${args.chargerId}`
}

const getChargerInfoParams = (args) => {
	return `locationId.stationId=${args.locationIdStationId}&chargerId=${args.chargerId}`
}

export const chargersApi = createApi({
	reducerPath: "chargers",
	baseQuery: baseQueryWithReauth,
	endpoints: (builder) => ({
		getChargers: builder.query<ChargersResponse, ChargersParams>({
			query: (args) => ({
				url: `get-chargers?${getUrlParams(args)}`,
				keepUnusedDataFor: 0
			}),
		}), 
		// &currentTime=${moment(new Date()).format()}
		getChargerInfo: builder.query<ChargerInfoResponse, Charger>({
			query: (args:Charger) => ({
				url: `get-charger-info?locationId.stationId=${args.locationIdStationId}&chargerId=${args.chargerId}`,
			}),
		}),
		// &currentTime=${moment(new Date()).format()
		getChargerTransactions: builder.query<ChargerTransactionResponse, any>({
			query: (args:ChargerTransaction) => ({
				url: `get-charger-transactions?${getUrlParamsForTransactions(args)}`,
			}),
		}),
		getUserTransactions: builder.query<ChargerTransactionResponse, ChargerTransaction>({
			query: (args:ChargerTransaction) => ({
				url: `get-user-transactions?${getUrlParamsForUserTransactions(args)}`,
			}),
		}),
		getUserInfo: builder.query<ChargerTransactionResponse, Charger>({
			query: (args:ChargerTransaction) => ({
				url: `get-user-info?${getUserInfoParams(args)}`,
			}),
		}),
		getChargerUser: builder.query<any, Charger>({
			query: (args:ChargerTransaction) => ({
				url: `get-charger-user?${getChargerInfoParams(args)}`,
			}),
		}),
		assignCharger: builder.mutation<chargerActionType, AssignCharger>({
			query: (data) => {
				const {locationIdStationId, chargerId, email, expireAt, contactNum, licensePlate, parkingNum, name, room} = data;
				return {
					url: "assign-charger",
					method: "PUT",
					body: {
						"locationId.stationId": locationIdStationId,
						"chargerId": chargerId,
						"email": email,
						"expireAt": expireAt,
						"contactNum": contactNum ? contactNum : "",
						"licensePlate": licensePlate ? licensePlate : "",
						"parkingNum": parkingNum ? parkingNum : "",
						"name": name ? name : "",
						"room": room ? room : "",
					}
				};
			}
		}),
		unassignCharger: builder.mutation<chargerActionType, Charger>({
			query: (data) => {
				const {locationIdStationId, chargerId, userName} = data;
				return {
					url: "unassign-charger",
					method: "PUT",
					body: {
						"locationId.stationId": locationIdStationId,
						"chargerId": chargerId,
						"userName": userName,
					}
				};
			}
		}),
		startCharger: builder.mutation<chargerActionType, Charger>({
			query: (data) => {
				const {userName, locationIdStationId, chargerId} = data;
				return {
					url: "start-charger",
					method: "PUT",
					body: {"userName": userName, "locationId.stationId": locationIdStationId, "chargerId": chargerId}
				};
			}
		}),
		rebootCharger: builder.mutation<chargerActionType, Charger>({
			query: (data) => {
				const {userName, locationIdStationId, chargerId} = data;
				return {
					url: "restart-charger",
					method: "PUT",
					body: {"userName": userName, "locationId.stationId": locationIdStationId, "chargerId": chargerId}
				};
			}
		}),
		stopCharger: builder.mutation<chargerActionType, Charger>({
			query: (data) => {
				const {userName, locationIdStationId, chargerId} = data;
				return {
					url: "stop-charger",
					method: "PUT",
					body: {"userName": userName, "locationId.stationId": locationIdStationId, "chargerId": chargerId}
				};
			}
		})
	}),
});

export const {
	useGetChargersQuery,
	useGetChargerInfoQuery,
	useGetChargerTransactionsQuery,
	useAssignChargerMutation,
	useUnassignChargerMutation,
	useStartChargerMutation,
	useRebootChargerMutation,
	useStopChargerMutation,
	useGetUserInfoQuery,
	useGetUserTransactionsQuery,
	useGetChargerUserQuery,
} = chargersApi;
